import React, { useEffect, useState, useCallback, memo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Divider,
  Chip,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Button,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import {
  ShoppingCartOutlined,
  AssignmentTurnedInOutlined,
  LocalShippingOutlined,
  CheckCircleOutline,
  CurrencyRupee,
  Cancel,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { toast } from "react-toastify";
import {
  getDashboardStats,
  getDashboardBranch,
} from "../service/dashboardService";
import { getAllBranchLists } from "../service/branchService";
import Color from "../components/Color";

/* ==================== DATE PICKER IMPORTS ==================== */
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

/* ==================== CHART.JS IMPORTS ==================== */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  DoughnutController,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  DoughnutController
);

/* ==================== MEMOIZED CARD - FULLY RESPONSIVE WITH GRID ==================== */
const MemoizedCard = memo(({ stat }) => (
  <Card
    sx={{
      borderRadius: '24px',
      boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
      background: stat.backgroundColor,
      color: "white",
      height: { xs: 90, sm: 110, md: 100, lg: 100 },
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
      }
    }}
  >
    <CardContent sx={{ p: { xs: 2.5, sm: 2.5, md: 2.5, lg: 2.5 }, flex: 1 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem", lg: ".9rem" },
              fontWeight: 500,
              letterSpacing: '0.02em',
              opacity: 0.9,
              lineHeight: 1.3,
              mb: 0.8,
              fontFamily: '"Poppins", sans-serif',
            }}
          >
            {stat.label}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.3rem", lg: "1.4rem" },
              fontFamily: '"Poppins", sans-serif',
            }}
          >
            {stat.prefix || ""}
            <CountUp
              end={parseFloat(stat.value) || 0}
              duration={1.8}
              decimals={stat.prefix ? 2 : 0}
              separator=","
            />
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: 36, sm: 40, md: 44, lg: 44 },
            height: { xs: 36, sm: 40, md: 44, lg: 44 },
            ml: 2,
          }}
        >
          {React.cloneElement(stat.icon, { sx: { fontSize: { xs: 24, sm: 28 } } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
));

/* ==================== SMOOTH BLUE LINE CHART ==================== */
const MemoizedBarChart = memo(({ monthly_sales }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const labels = monthly_sales.map((item) => item.month);
  const values = monthly_sales.map((item) => item.total || 0);

  const data = {
    labels,
    datasets: [
      {
        label: "Sales",
        data: values,
        borderColor: "#425c8d",
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(66, 92, 141, 0.3)");
          gradient.addColorStop(1, "rgba(66, 92, 141, 0.02)");
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#425c8d",
        pointBorderColor: "#fff",
        pointBorderWidth: 3,
        pointRadius: 6,
        pointHoverRadius: 9,
        borderWidth: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.85)",
        titleColor: "#fff",
        bodyColor: "#fff",
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => `₹${context.parsed.y.toFixed(2)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `₹${value}`,
          font: { size: isXs ? 10 : 12 },
          color: "#666",
        },
        grid: { display: false },
        border: { display: false },
      },
      x: {
        ticks: {
          font: { size: isXs ? 10 : 12 },
          color: "#666",
        },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  return (
    <Box width={{ xs: "100%", sm: "100%", md: "49%", lg: "49.2%" }}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
          height: { xs: 320, sm: 420, md: 420, lg: 450 },
          background: "#f8fbff",
          border: "1px solid #e0e0e0",
          mt: { xs: 1, sm: 2 },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Monthly Sales
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Line data={data} options={options} />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

/* ==================== DOUGHNUT CHART ==================== */
const MemoizedPieChart = memo(({ top_selling_products }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const COLORS = ["#4B7BEC", "#FF6B6B", "#1DD1A1", "#ff23c8ff", "#FF8C00"];

  const data = {
    labels: top_selling_products.slice(0, 5).map((p) => p.name || "Unknown"),
    datasets: [
      {
        data: top_selling_products.slice(0, 5).map((p) => Number(p.total_revenue) || 0),
        backgroundColor: COLORS,
        hoverOffset: 15,
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isXs ? "bottom" : "left",
        align: "center",
        labels: {
          font: { size: isXs ? 10 : 12 },
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
          color: "#333",
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ₹${value.toFixed(2)} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <Box width={{ xs: "100%", sm: "100%", md: "49%", lg: "49.2%" }}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
          height: { xs: 380, sm: 420, md: 420, lg: 450 },
          border: "1px solid #e0e0e0",
          mt: { xs: 1, sm: 2 },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Top Selling Products
          </Typography>
          {top_selling_products.length > 0 ? (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Doughnut data={data} options={options} />
            </Box>
          ) : (
            <Typography color="textSecondary" sx={{ fontSize: "0.9rem" }}>
              No products found
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});

/* ==================== RECENT ORDERS - DATAGRID VERSION ==================== */
const MemoizedRecentOrders = memo(({ recent_orders }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const columns = [
    {
      field: "order_number",
      headerName: "Order ID",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Link
          to={`/view-order/${params.row.id}`}
          style={{
            textDecoration: "none",
            color: "#7c0bcc",
            fontWeight: "bold",
            fontSize: isMobile ? "0.7rem" : "0.875rem",
          }}
          onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
          onMouseOut={(e) => (e.target.style.textDecoration = "none")}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: "order_type",
      headerName: "Order Type",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "customer_name",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row?.customer?.name || "Unknown",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        let chipColor = "default";
        const status = params.value || "";
        if (status === "completed") chipColor = "success";
        else if (status === "cancelled") chipColor = "error";
        else if (["pending", "order_placed", "ready_for_delivery"].includes(status)) chipColor = "warning";

        return (
          <Chip
            label={status.replace(/_/g, " ").toUpperCase()}
            color={chipColor}
            size="small"
            sx={{ fontSize: isMobile ? "0.65rem" : "0.7rem" }}
          />
        );
      },
    },
    // {
    //   field: "total_amount",
    //   headerName: "Total",
    //   flex: 1,
    //   minWidth: 100,
    //   valueFormatter: (params) => {
    //     const value = parseFloat(params.value);
    //     if (isNaN(value)) return "₹0.00";
    //     return `₹${value.toFixed(2)}`;
    //   },
    // },
  ];

  const rows = (recent_orders || []).map((order) => ({
    id: order.id,
    order_number: order.order_number || "N/A",
    order_type: order.order_type,
    customer: order.customer || {},
    status: order.status || "pending",
    total_amount: parseFloat(order.total_amount) || 0,
  }));

  return (
    <Grid item xs={12} mt={{ xs: 2, sm: 3, md: 4 }}>
      <Card sx={{ borderRadius: 2, boxShadow: "0px 2px 8px rgba(0,0,0,0.1)", border: "1px solid #e0e0e0" }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Recent Orders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5 },
                },
              }}
              pageSizeOptions={[5, 10]}
              disableSelectionOnClick
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnSorting
              autoHeight={false}
              sx={{
                border: "none",
                fontFamily: '"Poppins", sans-serif',
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 600,
                  pl: 0,
                },
                "& .MuiDataGrid-cell": {
                  fontSize: isMobile ? "12px" : "14px",
                  pl: 1,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f9f9f9",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#fff",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f4f4f4",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
});

/* ==================== MAIN DASHBOARD COMPONENT ==================== */
/* ==================== MAIN DASHBOARD COMPONENT ==================== */
const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    total_sales: 0,
    today_sales: 0,
    total_orders: 0,
    total_products: 0,
    received_orders: 0,
    today_received_orders_count: 0,
    ready_for_delivery_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    received_amount: 0,
    pending_amount: 0,
    today_received_amount: 0,
    filtered_orders: [],
    recent_orders: [],
    monthly_sales: [
      { month: "Jul", total: 0 },
      { month: "Aug", total: 0 },
      { month: "Sep", total: 0 },
    ],
    top_selling_products: [],
  });
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isBranchesLoading, setIsBranchesLoading] = useState(false);
  const [userBranchId, setUserBranchId] = useState(null);

  // Default is "today"
  const [dateFilter, setDateFilter] = useState("today");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Order type filter: 'invoice' = default, 'quotation'
  const [orderTypeFilter, setOrderTypeFilter] = useState("invoice");

  const userRole = localStorage.getItem("role");
  const isAdmin = userRole && userRole === "1";

  const getDateRange = useCallback((filterType) => {
    const today = dayjs();
    switch (filterType) {
      case "today":
        return {
          from: today.format("YYYY-MM-DD"),
          to: today.format("YYYY-MM-DD"),
        };
      case "this_week":
        return {
          from: today.startOf("week").format("YYYY-MM-DD"),
          to: today.endOf("week").format("YYYY-MM-DD"),
        };
      case "custom":
        return {
          from: customFromDate ? dayjs(customFromDate).format("YYYY-MM-DD") : null,
          to: customToDate ? dayjs(customToDate).format("YYYY-MM-DD") : null,
        };
      default:
        return {
          from: today.format("YYYY-MM-DD"),
          to: today.format("YYYY-MM-DD"),
        };
    }
  }, [customFromDate, customToDate]);

  const handleDateFilterClick = (filterType) => {
    if (filterType === "custom") {
      setShowCustomPicker((prev) => !prev);
    } else {
      setDateFilter(filterType);
      setShowCustomPicker(false);
      setCustomFromDate(null);
      setCustomToDate(null);
    }
  };

  const handleCustomDateClose = () => setShowCustomPicker(false);

  const handleApplyCustomDate = () => {
    if (customFromDate && customToDate) {
      setDateFilter("custom");
      setShowCustomPicker(false);
    }
  };

  useEffect(() => {
    const fetchBranches = async () => {
      setIsBranchesLoading(true);
      try {
        const res = await getAllBranchLists();
        const list = res?.data?.data || [];
        const branchList = isAdmin
          ? [{ id: "all", name: "All Branches" }, ...list.map((b) => ({ id: b.id, name: b.name }))]
          : list.map((b) => ({ id: b.id, name: b.name }));
        setBranches(branchList);

        if (!isAdmin) {
          const userBranch = localStorage.getItem("branch");
          if (userBranch) {
            setUserBranchId(userBranch);
            setSelectedBranch(userBranch);
          } else {
            toast.error("No branch assigned to this user!");
          }
        } else {
          setSelectedBranch("all");
        }
      } catch (error) {
        toast.error("Failed to fetch branches!");
      } finally {
        setIsBranchesLoading(false);
      }
    };
    fetchBranches();
  }, [isAdmin]);

  const fetchStats = useCallback(async (branchId, dateRange) => {
    if (!branchId) return;
    setIsStatsLoading(true);
    try {
      const params = {
        from_date: dateRange?.from || null,
        to_date: dateRange?.to || null,
      };

      // Only apply 'filter=week' when "This Week" is selected
      if (dateFilter === "this_week") {
        params.filter = "week";
      }
      // For "today" and "custom" → NO filter param (let backend use exact dates)

      // Apply order_type filter if selected
      if (orderTypeFilter) {
        params.order_type = orderTypeFilter;
      }

      let res;
      if (branchId === "all") {
        res = await getDashboardStats(params);
      } else {
        res = await getDashboardBranch(branchId, params);
      }

      const responseData = res.data?.data || res.data;

      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const now = new Date();
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const monthIndex = (now.getMonth() - i + 12) % 12;
        months.push({ month: monthNames[monthIndex], total: 0 });
      }

      const monthlySales = Array.isArray(responseData.monthly_sales) ? responseData.monthly_sales : [];
      const updatedMonthlySales = months.map((defaultMonth) => {
        const found = monthlySales.find(
          (sale) => String(sale.month).toLowerCase() === defaultMonth.month.toLowerCase()
        );
        return {
          month: defaultMonth.month,
          total: found ? Number(found.total) || 0 : 0,
        };
      });

      const topSellingProducts = Array.isArray(responseData.top_selling_products)
        ? responseData.top_selling_products
        : [];

      const parsedTopSellingProducts = topSellingProducts.map((p) => ({
        ...p,
        total_revenue: Number(p.total_revenue) || 0,
        name: p.name || "Unknown Product",
      }));

      const parsedData = {
        ...responseData,
        today_sales: responseData.today_sales || 0,
        today_received_orders_count: responseData.today_received_orders_count || 0,
        today_received_amount: responseData.today_received_amount || 0,
        monthly_sales: updatedMonthlySales,
        top_selling_products: parsedTopSellingProducts,
        recent_orders: responseData.recent_orders || [],
        filtered_orders: Array.isArray(responseData.filtered_orders)
          ? responseData.filtered_orders
          : (responseData.recent_orders || []),
      };

      setDashboardData((prev) => ({ ...prev, ...parsedData }));
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      toast.error("Failed to fetch dashboard data!");
    } finally {
      setIsStatsLoading(false);
    }
  }, [dateFilter, orderTypeFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!selectedBranch) return;

      const dateRange = getDateRange(dateFilter);

      if (dateFilter === "custom") {
        if (!dateRange.from || !dateRange.to) {
          return;
        }
      }

      fetchStats(selectedBranch, dateRange);
    }, 300);

    return () => clearTimeout(handler);
  }, [
    selectedBranch,
    dateFilter,
    customFromDate,
    customToDate,
    orderTypeFilter,
    fetchStats,
    getDateRange,
  ]);

  // Conditional stats: Hide "Today Sales" and "Today Received Orders" when not in "today" mode
  const getVisibleStats = () => {
    const allStats = [
      {
        label: " Sales Amount",
        value: dashboardData.total_sales,
        prefix: "₹",
        icon: <CurrencyRupee sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#2e4ea8",
      },
      // {
      //   label: "Today Sales",
      //   value: dashboardData.today_sales,
      //   prefix: "₹",
      //   icon: <CurrencyRupee sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
      //   backgroundColor: "#11998e",
      //   showOnlyOnToday: true,
      // },
      {
        label: "Received Amount",
        value: dashboardData.received_amount,
        prefix: "₹",
        icon: <CurrencyRupee sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#00bcd4",
      },
      {
        label: "Pending Amount",
        value: dashboardData.pending_amount,
        prefix: "₹",
        icon: <CurrencyRupee sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#ff9800",
      },
      // {
      //   label: "Today Received Orders",
      //   value: dashboardData.today_received_orders_count,
      //   icon: <AssignmentTurnedInOutlined sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
      //   backgroundColor: "#b39907ff",
      //   showOnlyOnToday: true,
      // },
      {
        label: " Received Orders",
        value: dashboardData.received_orders,
        icon: <AssignmentTurnedInOutlined sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#0288d1",
      },
      {
        label: "Total Orders",
        value: dashboardData.total_orders,
        icon: <ShoppingCartOutlined sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#1DD1A1",
      },
      {
        label: "Ready for Delivery",
        value: dashboardData.ready_for_delivery_orders,
        icon: <LocalShippingOutlined sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#ff9800",
      },
      {
        label: "Completed Orders",
        value: dashboardData.completed_orders,
        icon: <CheckCircleOutline sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#4caf50",
      },
      {
        label: "Cancelled Orders",
        value: dashboardData.cancelled_orders,
        icon: <Cancel sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, color: "#fff" }} />,
        backgroundColor: "#f44336",
      },
    ];

    return allStats.filter(stat => !stat.showOnlyOnToday || dateFilter === "today");
  };

  const stats = getVisibleStats();

  const displayText = (() => {
    if (isAdmin && selectedBranch === "all") {
      return "Admin";
    }

    const branch = branches.find((b) => String(b.id) === String(selectedBranch));
    if (branch) {
      return branch.name;
    }

    if (isBranchesLoading) {
      return "Loading...";
    }

    return "Branch";
  })();

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden", p: { xs: 1, sm: 2 }, mb: { xs: 1, sm: 2, md: 2, lg: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 1, sm: 2, md: 2 },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            color: Color.label.main,
            fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem", lg: "2rem", xl: "2.2rem" },
          }}
        >
          <span style={{ color: "red" }}>Welcome, </span>
          <span style={{ color: Color.primary.main }}>{displayText}!</span>
        </Typography>

        <FormControl sx={{ width: { xs: "100%", sm: 200, md: 250, lg: 300 } }} size="small">
          <Select
            value={selectedBranch || ""}
            onChange={(e) => setSelectedBranch(e.target.value)}
            disabled={isBranchesLoading || !isAdmin}
            sx={{ "& .MuiSelect-select": { fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }, py: { xs: 0.5, sm: 1 } } }}
          >
            {isAdmin && <MenuItem value="all">All Branches</MenuItem>}
            {isBranchesLoading ? (
              <MenuItem disabled><CircularProgress size={20} /></MenuItem>
            ) : (
              branches.filter((b) => b.id !== "all").map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Combined: Order Type + Date Filters — single row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          mt: { xs: 1.5, sm: 2 },
          mb: { xs: 1, sm: 1.5 },
        }}
      >
        {/* Left: Order Type pills */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
          {/* <Typography
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              fontWeight: 600,
              color: "#555",
              whiteSpace: "nowrap",
              letterSpacing: "0.02em",
            }}
          >
            Order Type:
          </Typography> */}
          {[
            { value: "invoice", label: "Invoice" },
            { value: "quotation", label: "Quotation" },
          ].map((opt) => (
            <Button
              key={opt.value}
              onClick={() => setOrderTypeFilter(opt.value)}
              size="small"
              sx={{
                fontSize: { xs: "0.72rem", sm: "0.8rem" },
                px: { xs: 1.5, sm: 2.5 },
                py: 0.5,
                textTransform: "none",
                borderRadius: "20px",
                fontWeight: orderTypeFilter === opt.value ? 700 : 500,
                border: orderTypeFilter === opt.value
                  ? "2px solid #7C0BCC"
                  : "1.5px solid #ddd",
                backgroundColor: orderTypeFilter === opt.value ? "#7C0BCC" : "#fff",
                color: orderTypeFilter === opt.value ? "#fff" : "#555",
                boxShadow: orderTypeFilter === opt.value
                  ? "0 2px 8px rgba(124,11,204,0.25)"
                  : "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: orderTypeFilter === opt.value ? "#6a0ab0" : "rgba(124, 11, 204, 0.06)",
                  borderColor: "#7C0BCC",
                  color: orderTypeFilter === opt.value ? "#fff" : "#7C0BCC",
                },
              }}
            >
              {opt.label}
            </Button>
          ))}
        </Box>

        {/* Right: Date filter buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
          <Button
            onClick={() => handleDateFilterClick("today")}
            variant={dateFilter === "today" ? "contained" : "outlined"}
            size="small"
            sx={{
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
              px: { xs: 1.5, sm: 2, md: 3 },
              textTransform: "none",
              minWidth: { xs: 60, sm: 80 },
              backgroundColor: dateFilter === "today" ? "#7C0BCC" : "transparent",
              color: dateFilter === "today" ? "#fff" : "#7C0BCC",
              borderColor: "#7C0BCC",
              "&:hover": { backgroundColor: dateFilter === "today" ? "#6a0ab0" : "rgba(124, 11, 204, 0.08)", borderColor: "#7C0BCC" },
            }}
          >
            Today
          </Button>
          <Button
            onClick={() => handleDateFilterClick("this_week")}
            variant={dateFilter === "this_week" ? "contained" : "outlined"}
            size="small"
            sx={{
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
              px: { xs: 1.5, sm: 2, md: 3 },
              textTransform: "none",
              minWidth: { xs: 70, sm: 90 },
              backgroundColor: dateFilter === "this_week" ? "#7C0BCC" : "transparent",
              color: dateFilter === "this_week" ? "#fff" : "#7C0BCC",
              borderColor: "#7C0BCC",
              "&:hover": { backgroundColor: dateFilter === "this_week" ? "#6a0ab0" : "rgba(124, 11, 204, 0.08)", borderColor: "#7C0BCC" },
            }}
          >
            This Week
          </Button>

          {/* Custom button + floating date picker */}
          <ClickAwayListener onClickAway={handleCustomDateClose}>
            <Box sx={{ position: "relative" }}>
              <Button
                onClick={() => handleDateFilterClick("custom")}
                variant={dateFilter === "custom" ? "contained" : "outlined"}
                size="small"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                  px: { xs: 1.5, sm: 2, md: 3 },
                  textTransform: "none",
                  minWidth: { xs: 60, sm: 80 },
                  backgroundColor: dateFilter === "custom" ? "#7C0BCC" : "transparent",
                  color: dateFilter === "custom" ? "#fff" : "#7C0BCC",
                  borderColor: "#7C0BCC",
                  "&:hover": { backgroundColor: dateFilter === "custom" ? "#6a0ab0" : "rgba(124, 11, 204, 0.08)", borderColor: "#7C0BCC" },
                }}
              >
                Custom
              </Button>
              {showCustomPicker && (
                <Paper
                  elevation={8}
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    right: 0,
                    zIndex: 1300,
                    p: 2,
                    minWidth: 280,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Select Date Range</Typography>
                      <DatePicker
                        label="From Date"
                        value={customFromDate}
                        onChange={setCustomFromDate}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                          popper: { disableScrollLock: true },
                        }}
                        maxDate={customToDate || undefined}
                      />
                      <DatePicker
                        label="To Date"
                        value={customToDate}
                        onChange={setCustomToDate}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                          popper: { disableScrollLock: true },
                        }}
                        minDate={customFromDate || undefined}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button size="small" onClick={() => { setCustomFromDate(null); setCustomToDate(null); }}>Clear</Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleApplyCustomDate}
                          disabled={!customFromDate || !customToDate}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </LocalizationProvider>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>

          {dateFilter === "custom" && customFromDate && customToDate && (
            <Typography
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                color: "text.secondary",
                whiteSpace: "nowrap",
                ml: 1,
                border: "1px solid #ccc",
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {dayjs(customFromDate).format("DD/MM/YYYY")} - {dayjs(customToDate).format("DD/MM/YYYY")}
            </Typography>
          )}
        </Box>
      </Box>

      {/* RESPONSIVE STATS CARDS - BALANCED GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
            lg: stats.length >= 9 ? "repeat(5, 1fr)" : "repeat(4, 1fr)", // 10 cards → 5 cols, 8 cards → 4 cols
            xl: stats.length >= 9 ? "repeat(5, 1fr)" : "repeat(4, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3, lg: 2 },
          mt: 2,
        }}
      >
        {stats.map((stat, i) => (
          <MemoizedCard key={i} stat={stat} />
        ))}
      </Box>

      {/* Charts — side-by-side on md+, stacked on sm/xs */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 0, md: 2 },
          mt: 2,
          justifyContent: "space-between",
        }}
      >
        <MemoizedBarChart monthly_sales={dashboardData.monthly_sales} />
        <MemoizedPieChart top_selling_products={dashboardData.top_selling_products} />
      </Box>

      {/* Recent Orders - DataGrid */}
      <MemoizedRecentOrders recent_orders={dashboardData.recent_orders} />
    </Box>
  );
};

export default Dashboard;





// import React, { useEffect, useState, useCallback, memo } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Box,
//   Avatar,
//   Table,
//   TableHead,
//   TableRow,
//   TableCell,
//   TableBody,
//   Divider,
//   Chip,
//   FormControl,
//   Select,
//   MenuItem,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
// } from "@mui/material";
// import {
//   ShoppingCartOutlined,
//   AssignmentTurnedInOutlined,
//   LocalShippingOutlined,
//   CheckCircleOutline,
//   CurrencyRupee,
//   Cancel,
// } from "@mui/icons-material";
// import { Link } from "react-router-dom";
// import CountUp from "react-countup";
// import { toast } from "react-toastify";
// import {
//   getDashboardStats,
//   getDashboardBranch,
// } from "../service/dashboardService";
// import { getAllBranchLists } from "../service/branchService";
// import Color from "../components/Color";

// /* ==================== CHART.JS IMPORTS ==================== */
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   BarElement,
//   Title,
//   Tooltip,
//   Legend,
//   ArcElement,
//   DoughnutController,
// } from "chart.js";
// import { Bar, Doughnut } from "react-chartjs-2";

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   BarElement,
//   Title,
//   Tooltip,
//   Legend,
//   ArcElement,
//   DoughnutController
// );
// /* =========================================================== */

// const MemoizedCard = memo(({ stat }) => (
//   <Box
//     display="flex"
//     flex="1 1 auto"
//     width={{ xs: "47%", sm: "45%", md: "24%", lg: "22.7%" }}
//   >
//     <Card
//       sx={{
//         width: "100%",
//         maxWidth: "100%",
//         height: { xs: 80, sm: 90, md: 100, lg: 110 },
//         borderRadius: { xs: 1, sm: 2 },
//         boxShadow: "none",
//         background: stat.gradient,
//         color: "white",
//         boxSizing: "border-box",
//         display: "flex",
//         flexDirection: "column",
//         justifyContent: "center",
//       }}
//     >
//       <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1 }}>
//         <Box display="flex" alignItems="center" justifyContent="space-between">
//           <Box>
//             <Typography
//               variant="body2"
//               sx={{
//                 fontSize: {
//                   xs: "0.75rem",
//                   sm: "0.9rem",
//                   md: "1rem",
//                   lg: "1.1rem",
//                 },
//                 mt: { xs: 0.5, sm: 0.8, md: 1 },
//                 lineHeight: 1.2,
//               }}
//             >
//               {stat.label}
//             </Typography>
//             <Typography
//               variant="h5"
//               fontWeight="bold"
//               sx={{
//                 fontSize: {
//                   xs: "0.9rem",
//                   sm: "1.1rem",
//                   md: "1.3rem",
//                   lg: "1.5rem",
//                 },
//                 mt: { xs: 0.5, sm: 0.8, md: 1 },
//               }}
//             >
//               {stat.prefix || ""}
//               <CountUp
//                 end={parseFloat(stat.value) || 0}
//                 duration={1.5}
//                 decimals={stat.prefix ? 2 : 0}
//               />
//             </Typography>
//           </Box>
//           <Avatar
//             sx={{
//               bgcolor: "white",
//               width: { xs: 28, sm: 40, md: 50, lg: 60, xl: 70 },
//               height: { xs: 28, sm: 40, md: 50, lg: 60, xl: 70 },
//             }}
//           >
//             {stat.icon}
//           </Avatar>
//         </Box>
//       </CardContent>
//     </Card>
//   </Box>
// ));

// /* ==================== BAR CHART - Empty months = Light Gray ==================== */
// const MemoizedBarChart = memo(({ monthly_sales }) => {
//   const theme = useTheme();
//   const isXs = useMediaQuery(theme.breakpoints.down("sm"));

//   const data = {
//     labels: monthly_sales.map((item) => item.month),
//     datasets: [
//       {
//         label: "Sales",
//         data: monthly_sales.map((item) => item.total || 0),
//         backgroundColor: monthly_sales.map((item) =>
//           item.total > 0 ? "#d24419ff" : "#e0e0e0" // Light gray if no sales
//         ),
//         borderRadius: 6,
//         borderSkipped: false,
//       },
//     ],
//   };

//   const options = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { display: false },
//       tooltip: {
//         callbacks: {
//           label: (context) => {
//             const value = context.parsed.y;
//             return value > 0 ? `₹${value.toFixed(2)}` : "No Sales";
//           },
//         },
//       },
//     },
//     scales: {
//       y: {
//         ticks: {
//           callback: (value) => `₹${value}`,
//           font: { size: isXs ? 10 : 12 },
//         },
//         grid: { display: false },
//       },
//       x: {
//         ticks: { font: { size: isXs ? 10 : 12 } },
//         grid: { display: false },
//       },
//     },
//   };

//   return (
//     <Box
//       display="flex"
//       width={{ xs: "100%", sm: "48.5%", md: "47%", lg: "49.2%" }}
//     >
//       <Card
//         sx={{
//           borderRadius: { xs: 1, sm: 2 },
//           boxShadow: "none",
//           height: { xs: 300, sm: 330, md: 400, lg: 450, xl: 480 },
//           width: "100%",
//           maxWidth: "100%",
//           boxSizing: "border-box",
//           mt: { xs: 1, sm: 2 },
//         }}
//       >
//         <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
//           <Typography
//             variant="h6"
//             gutterBottom
//             fontWeight="bold"
//             mb={{ xs: 1, sm: 2, md: 3 }}
//             sx={{
//               fontSize: {
//                 xs: "0.8rem",
//                 sm: "0.9rem",
//                 md: "1.1rem",
//                 lg: "1.25rem",
//                 xl: "1.3rem",
//               },
//             }}
//           >
//             Monthly Sales
//           </Typography>
//           <Box sx={{ height: 280 }}>
//             <Bar data={data} options={options} />
//           </Box>
//         </CardContent>
//       </Card>
//     </Box>
//   );
// });

// /* ==================== DOUGHNUT CHART - Product Names on LEFT ==================== */
// const MemoizedPieChart = memo(({ top_selling_products }) => {
//   const theme = useTheme();
//   const isXs = useMediaQuery(theme.breakpoints.down("sm")); // Mobile

//   const COLORS = ["#4B7BEC", "#FF6B6B", "#1DD1A1", "#ff23c8ff", "#FF8C00"];

//   const data = {
//     labels: top_selling_products.slice(0, 5).map((p) => p.name || "Unknown"),
//     datasets: [
//       {
//         data: top_selling_products.slice(0, 5).map((p) => Number(p.total_revenue) || 0),
//         backgroundColor: COLORS,
//         hoverOffset: 15,
//         borderWidth: 2,
//         borderColor: "#fff",
//       },
//     ],
//   };

//   const options = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: {
//         position: isXs ? "bottom" : "left", // Left side on desktop/tablet
//         align: "center",
//         labels: {
//           font: { size: isXs ? 10 : 12 },
//           padding: 20,
//           usePointStyle: true,
//           pointStyle: "circle",
//           color: "#333",
//         },
//       },
//       tooltip: {
//         callbacks: {
//           label: (context) => {
//             const value = context.parsed;
//             const total = context.dataset.data.reduce((a, b) => a + b, 0);
//             const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
//             return `${context.label}: ₹${value.toFixed(2)} (${percentage}%)`;
//           },
//         },
//       },
//     },
//   };

//   return (
//     <Box
//       display="flex"
//       width={{ xs: "100%", sm: "48.5%", md: "47%", lg: "49.2%" }}
//     >
//       <Card
//         sx={{
//           borderRadius: { xs: 1, sm: 2 },
//           boxShadow: "none",
//           height: { xs: 400, sm: 330, md: 400, lg: 450, xl: 480 },
//           width: "100%",
//           maxWidth: "100%",
//           mt: { xs: 1, sm: 2 },
//         }}
//       >
//         <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
//           <Typography
//             variant="h6"
//             fontWeight="bold"
//             mb={{ xs: 1, sm: 2 }}
//             sx={{
//               fontSize: {
//                 xs: "0.8rem",
//                 sm: "0.9rem",
//                 md: "1.1rem",
//                 lg: "1.25rem",
//               },
//             }}
//           >
//             Top Selling Products
//           </Typography>

//           {top_selling_products.length > 0 ? (
//             <Box sx={{ height: 300, position: "relative" }}>
//               <Doughnut data={data} options={options} />
//             </Box>
//           ) : (
//             <Typography
//               color="textSecondary"
//               sx={{ fontSize: { xs: "0.65rem", sm: "0.9rem", md: "1rem" } }}
//             >
//               No products found
//             </Typography>
//           )}
//         </CardContent>
//       </Card>
//     </Box>
//   );
// });

// /* ==================== RECENT ORDERS (Unchanged) ==================== */
// const MemoizedRecentOrders = memo(({ recent_orders }) => (
//   <Grid item xs={12} mt={{ xs: 2, sm: 3, md: 4 }} width="100%">
//     <Card
//       sx={{
//         borderRadius: { xs: 1, sm: 2 },
//         boxShadow: "none",
//         width: "100%",
//         maxWidth: "100%",
//         display: "flex",
//         flexDirection: "column",
//         mb: { xs: 1, sm: 2 },
//         boxSizing: "border-box",
//       }}
//     >
//       <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
//         <Typography
//           variant="h6"
//           gutterBottom
//           fontWeight="bold"
//           sx={{
//             fontSize: {
//               xs: "0.8rem",
//               sm: "0.9rem",
//               md: "1.1rem",
//               lg: "1.25rem",
//             },
//           }}
//         >
//           Recent Orders
//         </Typography>
//         <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
//         <Table
//           sx={{
//             minWidth: { xs: 280, sm: 400, md: 600, lg: 750 },
//             width: "100%",
//             maxWidth: "100%",
//             "& .MuiTableCell-root": {
//               padding: { xs: "6px 4px", sm: "8px", md: "12px", lg: "16px" },
//               fontSize: {
//                 xs: "0.65rem",
//                 sm: "0.75rem",
//                 md: "0.9rem",
//                 lg: "1rem",
//               },
//             },
//           }}
//         >
//           <TableHead>
//             <TableRow>
//               <TableCell>Order id</TableCell>
//               <TableCell>Customer</TableCell>
//               <TableCell>Status</TableCell>
//               <TableCell align="right">Total</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {recent_orders.length > 0 ? (
//               recent_orders.slice(0, 5).map((order, idx) => {
//                 let chipColor = "default";
//                 if (order.status === "completed") chipColor = "success";
//                 else if (order.status === "cancelled") chipColor = "error";
//                 else if (order.status === "pending" || order.status === "order_placed")
//                   chipColor = "warning";

//                 return (
//                   <TableRow
//                     key={order.id || idx}
//                     sx={{
//                       bgcolor: idx % 2 === 0 ? "grey.50" : "white",
//                     }}
//                   >
//                     <TableCell>
//                       <Link
//                         to={`/edit-order/${order.id}`}
//                         style={{
//                           textDecoration: "none",
//                           color: "#7c0bcc",
//                           fontWeight: "bold",
//                         }}
//                         onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
//                         onMouseOut={(e) => (e.target.style.textDecoration = "none")}
//                       >
//                         {order.order_number}
//                       </Link>
//                     </TableCell>
//                     <TableCell>
//                       {order.customer?.name || order.customer_name || "Unknown"}
//                     </TableCell>
//                     <TableCell>
//                       <Chip
//                         label={order.status.replace(/_/g, " ").toUpperCase()}
//                         color={chipColor}
//                         size="small"
//                         sx={{
//                           fontSize: {
//                             xs: "0.55rem",
//                             sm: "0.65rem",
//                             md: "0.75rem",
//                             lg: "0.8rem",
//                           },
//                           textTransform: "none",
//                         }}
//                       />
//                     </TableCell>
//                     <TableCell align="right">
//                       ₹{parseFloat(order.total_amount || 0).toFixed(2)}
//                     </TableCell>
//                   </TableRow>
//                 );
//               })
//             ) : (
//               <TableRow>
//                 <TableCell colSpan={4} align="center">
//                   No recent orders
//                 </TableCell>
//               </TableRow>
//             )}
//           </TableBody>
//         </Table>
//       </CardContent>
//     </Card>
//   </Grid>
// ));

// /* ==================== MAIN DASHBOARD COMPONENT ==================== */
// const Dashboard = () => {
//   const [dashboardData, setDashboardData] = useState({
//     total_sales: 0,
//     total_orders: 0,
//     total_products: 0,
//     received_orders: 0,
//     ready_for_delivery_orders: 0,
//     completed_orders: 0,
//     cancelled_orders: 0,
//     received_amount: 0,
//     pending_amount: 0,
//     recent_orders: [],
//     monthly_sales: [
//       { month: "Jul", total: 0 },
//       { month: "Aug", total: 0 },
//       { month: "Sep", total: 0 },
//     ],
//     top_selling_products: [],
//   });
//   const [selectedBranch, setSelectedBranch] = useState("");
//   const [branches, setBranches] = useState([]);
//   const [isStatsLoading, setIsStatsLoading] = useState(false);
//   const [isBranchesLoading, setIsBranchesLoading] = useState(false);
//   const [userBranchId, setUserBranchId] = useState(null);

//   const userRole = localStorage.getItem("role");
//   const isAdmin = userRole && userRole === "1";

//   useEffect(() => {
//     const fetchBranches = async () => {
//       setIsBranchesLoading(true);
//       try {
//         const res = await getAllBranchLists();
//         const list = res?.data?.data || [];
//         const branchList = isAdmin
//           ? [{ id: "all", name: "All Branches" }, ...list.map((b) => ({ id: b.id, name: b.name }))]
//           : list.map((b) => ({ id: b.id, name: b.name }));
//         setBranches(branchList);

//         if (!isAdmin) {
//           const userBranch = localStorage.getItem("branch");
//           if (userBranch) {
//             setUserBranchId(userBranch);
//             setSelectedBranch(userBranch);
//           } else {
//             toast.error("No branch assigned to this user!");
//           }
//         } else {
//           setSelectedBranch("all");
//         }
//       } catch (error) {
//         toast.error("Failed to fetch branches!");
//       } finally {
//         setIsBranchesLoading(false);
//       }
//     };
//     fetchBranches();
//   }, [isAdmin]);

//   const fetchStats = useCallback(async (branchId) => {
//     if (!branchId) return;
//     setIsStatsLoading(true);
//     try {
//       let res;
//       if (branchId === "all") {
//         res = await getDashboardStats({ branchId: null });
//       } else {
//         res = await getDashboardBranch(branchId);
//       }

//       const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
//       const now = new Date();
//       const months = [];
//       for (let i = 5; i >= 0; i--) {
//         const monthIndex = (now.getMonth() - i + 12) % 12;
//         months.push({ month: monthNames[monthIndex], total: 0 });
//       }

//       const monthlySales = Array.isArray(res.data.data.monthly_sales)
//         ? res.data.data.monthly_sales
//         : [];

//       const updatedMonthlySales = months.map((defaultMonth) => {
//         const found = monthlySales.find(
//           (sale) => String(sale.month).toLowerCase() === defaultMonth.month.toLowerCase()
//         );
//         return {
//           month: defaultMonth.month,
//           total: found ? Number(found.total) || 0 : 0,
//         };
//       });

//       const topSellingProducts = Array.isArray(res.data.data.top_selling_products)
//         ? res.data.data.top_selling_products
//         : [];

//       const parsedTopSellingProducts = topSellingProducts.map((p) => ({
//         ...p,
//         total_revenue: Number(p.total_revenue) || 0,
//         name: p.name || "Unknown Product",
//       }));

//       const parsedData = {
//         ...res.data.data,
//         monthly_sales: updatedMonthlySales,
//         top_selling_products: parsedTopSellingProducts,
//         recent_orders: res.data.data.recent_orders || [],
//       };

//       setDashboardData((prev) => ({ ...prev, ...parsedData }));
//     } catch (err) {
//       console.error("Error fetching dashboard:", err);
//       toast.error("Failed to fetch dashboard data!");
//     } finally {
//       setIsStatsLoading(false);
//     }
//   }, []);

//   useEffect(() => {
//     const handler = setTimeout(() => {
//       if (selectedBranch) {
//         fetchStats(selectedBranch);
//       }
//     }, 300);
//     return () => clearTimeout(handler);
//   }, [selectedBranch, fetchStats]);

//   const stats = [
//     {
//       label: "Total Sales Amount",
//       value: dashboardData.total_sales,
//       prefix: "₹",
//       icon: <CurrencyRupee sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#2ba182ff" }} />,
//       gradient: "linear-gradient(135deg, #425c8dff, #1d3ed1ff)",
//     },
//     {
//       label: "Received Amount",
//       value: dashboardData.received_amount,
//       prefix: "₹",
//       icon: <CurrencyRupee sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#0288d1" }} />,
//       gradient: "linear-gradient(135deg, #4facfe, #00f2fe)",
//     },
//     {
//       label: "Pending Amount",
//       value: dashboardData.pending_amount,
//       prefix: "₹",
//       icon: <CurrencyRupee sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#f57c00" }} />,
//       gradient: "linear-gradient(135deg, #fbc02d, #f57c00)",
//     },
//     {
//       label: "Total Orders",
//       value: dashboardData.total_orders,
//       icon: <ShoppingCartOutlined sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#2ba182ff" }} />,
//       gradient: "linear-gradient(135deg, #1DD1A1, #2ba182ff)",
//     },
//     {
//       label: "Received Orders",
//       value: dashboardData.received_orders,
//       icon: <AssignmentTurnedInOutlined sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#0288d1" }} />,
//       gradient: "linear-gradient(135deg, #00c4b4, #0288d1)",
//     },
//     {
//       label: "Ready for Delivery",
//       value: dashboardData.ready_for_delivery_orders,
//       icon: <LocalShippingOutlined sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#f57c00" }} />,
//       gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
//     },
//     {
//       label: "Completed Orders",
//       value: dashboardData.completed_orders,
//       icon: <CheckCircleOutline sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#388e3c" }} />,
//       gradient: "linear-gradient(135deg, #66bb6a, #388e3c)",
//     },
//     {
//       label: "Cancelled Orders",
//       value: dashboardData.cancelled_orders,
//       icon: <Cancel sx={{ fontSize: { xs: 24, sm: 32, md: 36, lg: 38 }, color: "#ff1e00ff" }} />,
//       gradient: "linear-gradient(135deg, #ff1e00ff, #8e3838ff)",
//     },
//   ];

//   const displayText =
//     isAdmin && selectedBranch === "all"
//       ? "Admin"
//       : branches.find((b) => b.id === selectedBranch)?.name || "Select a branch";

//   return (
//     <Box
//       sx={{
//         width: "100%",
//         maxWidth: "100%",
//         mb: { xs: 0, sm: 4, md: 4 },
//         mt: { xs: 2, sm: 2 },
//         boxSizing: "border-box",
//         overflowX: "hidden",
//       }}
//     >
//       {/* Header */}
//       <Box
//         sx={{
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "center",
//           mb: { xs: 2, sm: 3, md: 4 },
//           flexWrap: "wrap",
//           gap: { xs: 1, sm: 2 },
//           width: "100%",
//         }}
//       >
//         <Typography
//           sx={{
//             fontWeight: "bold",
//             color: Color.label.main,
//             fontSize: {
//               xs: "1.2rem",
//               sm: "1.5rem",
//               md: "1.8rem",
//               lg: "2rem",
//               xl: "2.2rem",
//             },
//           }}
//         >
//           <span style={{ color: "red" }}>Welcome, </span>
//           <span style={{ color: Color.primary.main }}>{displayText}!</span>
//         </Typography>

//         <FormControl sx={{ width: { xs: "100%", sm: 250, md: 300, lg: 350, xl: 400 } }} size="small">
//           <Select
//             value={selectedBranch || ""}
//             onChange={(e) => setSelectedBranch(e.target.value)}
//             disabled={isBranchesLoading || !isAdmin}
//             sx={{
//               "& .MuiSelect-select": {
//                 fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
//                 py: { xs: 0.5, sm: 1 },
//               },
//             }}
//           >
//             {isAdmin && <MenuItem value="all">All Branches</MenuItem>}
//             {isBranchesLoading ? (
//               <MenuItem disabled>
//                 <CircularProgress size={20} />
//               </MenuItem>
//             ) : (
//               branches
//                 .filter((b) => b.id !== "all")
//                 .map((branch) => (
//                   <MenuItem key={branch.id} value={branch.id}>
//                     {branch.name}
//                   </MenuItem>
//                 ))
//             )}
//           </Select>
//         </FormControl>
//       </Box>

//       {/* Stat Cards */}
//       <Grid container spacing={{ xs: 1, sm: 2, md: 3, lg: 3 }} sx={{ justifyContent: { xs: "center", sm: "flex-start" } }}>
//         {stats.map((stat, i) => (
//           <MemoizedCard key={i} stat={stat} />
//         ))}
//       </Grid>

//       {/* Charts */}
//       <Grid
//         container
//         spacing={{ xs: 1, sm: 2, md: 3, lg: 3 }}
//         mt={{ xs: 2, sm: 3, md: 4 }}
//         sx={{ justifyContent: { xs: "center", sm: "space-between" } }}
//       >
//         <MemoizedBarChart monthly_sales={dashboardData.monthly_sales} />
//         <MemoizedPieChart top_selling_products={dashboardData.top_selling_products} />
//       </Grid>

//       {/* Recent Orders */}
//       <MemoizedRecentOrders recent_orders={dashboardData.recent_orders} />
//     </Box>
//   );
// };

// export default Dashboard;